// ---- The character designer — ONE page, TWO hosts ----
// The create-a-character design surface: a rotating 3D hero preview + the look
// tunables new players get (name, shirt/pants/skin/hair/eye swatches, hair +
// beard style, randomize). Built the minigame way (src/minigame/*): this module owns
// the whole page — markup, scoped CSS (injected once), its own small WebGL
// preview — and BOTH hosts mount the literal same thing:
//   • src/charselect.ts — the real create step between sign-in and the world
//   • src/studio/views/chardesign.ts — the studio's pixel-identical twin, the
//     iteration surface (what you tune there IS what new players see)
// Hosts differ only in the action buttons they pass (Create & play vs a studio
// note) — never in layout or styling. No game imports beyond the model kit, so
// the studio bundle stays clean.
//
// The preview renders the REAL humanoid archetype builder (src/model/
// humanoid.ts — the rounded shipping body, plan 27 folded in), mapped from the
// same Appearance the server stores: what you sculpt here IS the in-world rig.

import * as THREE from "three";
import { npcMeshGroup } from "./npc/define.ts";
import { disposeGroup } from "./meshkit.ts";
import { humanoid, type HumanoidOptions } from "./model/humanoid.ts";
import { toCssHex } from "./color.ts";
import {
  FOV, SWATCHES, PANTS_SWATCHES, SKIN_SWATCHES, HAIR_SWATCHES, EYE_SWATCHES, SEXES,
  hairStylesFor, beardStylesFor, fitHairStyle, fitBeardStyle,
} from "./config.ts";
import {
  DEFAULT_APPEARANCE, sanitizeAppearance,
  type Appearance, type BeardStyle, type HairStyle, type Sex,
} from "./shared.ts";

export type CharDesignerActions = {
  /** The gold CTA (charselect: "Create & play"). Omit to hide the row's CTA. */
  primary?: { label: string; onClick: () => void };
  /** The quiet second button (charselect: "Back"/"Log out"). Omit to hide. */
  secondary?: { label: string; onClick: () => void };
};

export type CharDesigner = {
  /** The whole page — append it wherever the host wants it. */
  el: HTMLElement;
  getLook(): Appearance;
  /** Replace the working look — syncs every swatch row + rebuilds the preview. */
  setLook(a: Appearance): void;
  getName(): string;
  setName(v: string): void;
  focusName(): void;
  setError(msg: string): void;
  /** Disable/enable the primary CTA (in-flight create). */
  setBusy(busy: boolean): void;
  setSecondaryLabel(label: string): void;
  /** Pause/resume the preview render loop (host visibility). */
  start(): void;
  stop(): void;
  resize(): void;
  /** Full teardown: GL context, observers, DOM. */
  dispose(): void;
};

/** Appearance → the humanoid's build options — THE preview mapping.
 *  (buildPlayer threads the same Appearance through the player doc's palette +
 *  body, so this preview and the in-world hero render the same rig.) */
function lookOptions(a: Appearance): HumanoidOptions {
  return {
    skin: a.skin,
    sex: a.sex,
    lower: { kind: "legs", color: a.pants },
    torso: { color: a.shirt },
    head: { hair: a.hair, hairStyle: a.hairStyle, beardStyle: a.beardStyle, eye: a.eye },
  };
}

// Scoped stylesheet, injected once per document. `.cd` re-declares the brand
// vars locally so the page is byte-identical outside index.html (the studio),
// and CARRIES the starlit-dusk backdrop itself (the .wlscene look) — the page
// is a FULL-SCREEN experience: the hero preview is the whole backdrop, the
// tunables float in a side panel. `.cd` fills whatever positioned box the host
// gives it (charselect: the fixed #csCreate overlay; studio: the view host).
const CSS = `
.cd {
  --wl-gold: #ffd86a; --wl-cream: #f6efdd; --wl-muted: #a89f8a;
  --wl-border: #333d4e; --wl-danger: #e06a54;
  --wl-font: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  position: absolute; inset: 0; overflow: hidden;
  font-family: var(--wl-font); color: var(--wl-cream); text-align: left;
  background:
    radial-gradient(135% 90% at 50% -18%, rgba(255, 216, 106, 0.15), transparent 55%),
    radial-gradient(90% 70% at 50% 120%, rgba(127, 184, 239, 0.06), transparent 72%),
    linear-gradient(180deg, #0c0f14 0%, #12151b 55%, #182029 100%);
}
.cd::before { /* starfield */
  content: ""; position: absolute; inset: 0; pointer-events: none; opacity: 0.55;
  background-image:
    radial-gradient(1.5px 1.5px at 12% 22%, rgba(255,246,221,.8), transparent),
    radial-gradient(1.5px 1.5px at 82% 16%, rgba(255,246,221,.7), transparent),
    radial-gradient(1px 1px at 26% 62%, rgba(255,246,221,.6), transparent),
    radial-gradient(1px 1px at 68% 74%, rgba(255,246,221,.55), transparent),
    radial-gradient(1.5px 1.5px at 45% 12%, rgba(255,246,221,.6), transparent),
    radial-gradient(1px 1px at 92% 52%, rgba(255,246,221,.5), transparent),
    radial-gradient(1px 1px at 8% 78%, rgba(255,246,221,.5), transparent),
    radial-gradient(1.5px 1.5px at 58% 40%, rgba(255,246,221,.45), transparent),
    radial-gradient(1px 1px at 36% 88%, rgba(255,246,221,.5), transparent),
    radial-gradient(1px 1px at 76% 92%, rgba(255,246,221,.4), transparent);
}
.cd-title {
  margin: 0 0 16px; font-size: 12px; font-weight: 600;
  letter-spacing: 0.16em; text-transform: uppercase; color: var(--wl-muted);
}
.cd-panel {
  position: absolute; left: 20px; top: 64px; bottom: 20px; z-index: 1;
  width: 340px; max-width: calc(100vw - 40px); box-sizing: border-box;
  display: flex; flex-direction: column; overflow-y: auto;
  padding: 22px 24px 18px; border-radius: 16px;
  background: linear-gradient(180deg, rgba(34,40,52,.92), rgba(22,27,35,.95));
  border: 1px solid rgba(255, 216, 106, 0.22);
  box-shadow: 0 24px 64px rgba(0,0,0,.55), inset 0 1px 0 rgba(255,255,255,.05);
  -webkit-backdrop-filter: blur(6px); backdrop-filter: blur(6px);
}
.cd-panel input {
  width: 100%; box-sizing: border-box; font: 15px var(--wl-font);
  padding: 11px; border-radius: 10px; outline: none; margin-bottom: 12px; color: var(--wl-cream);
  background: rgba(10,13,18,.6); border: 1px solid var(--wl-border); transition: border-color .15s ease, box-shadow .15s ease;
}
.cd-panel input::placeholder { color: #6f7787; }
.cd-panel input:focus { border-color: var(--wl-gold); box-shadow: 0 0 0 3px rgba(255,216,106,.15); }
.cd-spacer { flex: 1 1 0; min-height: 8px; }
.cd-row { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.cd-row > span {
  width: 42px; flex: none; text-align: right; text-transform: uppercase;
  font: 11px var(--wl-font); letter-spacing: 0.05em; color: var(--wl-muted);
}
.cd-swatches { display: flex; gap: 8px; flex-wrap: wrap; }
.cd-swatches button {
  width: 26px; height: 26px; padding: 0; border-radius: 50%; cursor: pointer;
  border: 2px solid rgba(255, 255, 255, 0.15); transition: transform .1s ease, border-color .1s ease;
}
.cd-swatches button:hover { transform: scale(1.15); }
.cd-swatches button.sel { border-color: var(--wl-gold); box-shadow: 0 0 0 2px rgba(255, 216, 106, 0.3); }
.cd-styles { display: flex; gap: 6px; flex-wrap: wrap; }
.cd-styles button {
  font: 600 11px var(--wl-font); cursor: pointer; padding: 6px 11px;
  border-radius: 8px; color: var(--wl-muted);
  background: rgba(255, 255, 255, 0.03); border: 1px solid var(--wl-border);
  transition: color .12s ease, border-color .12s ease, background .12s ease;
}
.cd-styles button:hover { color: var(--wl-cream); border-color: rgba(255, 216, 106, 0.35); }
.cd-styles button.sel {
  color: #2c2112; background: linear-gradient(180deg, #ffe08a, #ffd25a); border-color: var(--wl-gold);
}
.cd-random {
  font: 600 11px var(--wl-font); cursor: pointer; padding: 6px 11px; margin-top: 4px;
  border-radius: 8px; color: var(--wl-muted);
  background: rgba(255, 255, 255, 0.03); border: 1px dashed var(--wl-border);
  transition: color .12s ease, border-color .12s ease;
}
.cd-random:hover { color: var(--wl-gold); border-color: rgba(255, 216, 106, 0.35); }
.cd-preview { position: absolute; inset: 0; }
.cd-preview canvas {
  position: absolute; inset: 0; width: 100%; height: 100%; display: block;
  cursor: grab; touch-action: none;
}
.cd-preview canvas:active { cursor: grabbing; }
.cd-err { color: var(--wl-danger); font-size: 12.5px; line-height: 1.4; min-height: 16px; margin-top: 10px; }
.cd-actions { display: flex; gap: 10px; margin-top: 8px; }
.cd-primary {
  flex: 1; box-sizing: border-box; font: 800 15px var(--wl-font); cursor: pointer;
  padding: 13px; border: 0; border-radius: 10px; color: #2c2112;
  background: linear-gradient(180deg, #ffe08a, #ffd25a); box-shadow: 0 6px 18px rgba(255,216,106,.28);
  transition: filter .15s ease, box-shadow .15s ease, transform .1s ease;
}
.cd-primary:hover { filter: brightness(1.05); box-shadow: 0 8px 24px rgba(255,216,106,.42); }
.cd-primary:active { transform: translateY(1px); }
.cd-primary:disabled { opacity: .5; cursor: default; filter: none; box-shadow: none; }
.cd-secondary {
  box-sizing: border-box; font: 700 14px var(--wl-font); cursor: pointer; padding: 12px 16px;
  border-radius: 10px; color: var(--wl-cream); background: rgba(255,255,255,.05); border: 1px solid var(--wl-border);
  transition: border-color .12s ease, background .12s ease;
}
.cd-secondary:hover { border-color: rgba(255,216,106,.35); background: rgba(255,255,255,.08); }
`;

function ensureStyles(): void {
  if (document.getElementById("cd-style")) return;
  const style = document.createElement("style");
  style.id = "cd-style";
  style.textContent = CSS;
  document.head.appendChild(style);
}

/** The look knob rows, in display order — ONE table drives the markup, the
 *  selection sync, and randomize. */
const COLOR_ROWS: {
  key: Exclude<keyof Appearance, "hairStyle" | "beardStyle" | "sex">;
  label: string;
  colors: readonly number[];
}[] = [
  { key: "shirt", label: "shirt", colors: SWATCHES },
  { key: "pants", label: "pants", colors: PANTS_SWATCHES },
  { key: "skin", label: "skin", colors: SKIN_SWATCHES },
  { key: "hair", label: "hair", colors: HAIR_SWATCHES },
  { key: "eye", label: "eyes", colors: EYE_SWATCHES },
];

export function makeCharDesigner(opts: {
  title?: string;
  initial?: Appearance;
  actions?: CharDesignerActions;
} = {}): CharDesigner {
  ensureStyles();
  const look: Appearance = sanitizeAppearance({ ...DEFAULT_APPEARANCE, ...opts.initial });

  // ---- markup ---------------------------------------------------------------
  // Full-screen: the preview canvas IS the backdrop; the tunables float in a
  // left panel over it.
  const el = document.createElement("div");
  el.className = "cd";
  const previewBox = document.createElement("div");
  previewBox.className = "cd-preview";
  const canvas = document.createElement("canvas");
  previewBox.appendChild(canvas);
  const form = document.createElement("div");
  form.className = "cd-panel";
  const title = document.createElement("h1");
  title.className = "cd-title";
  title.textContent = opts.title ?? "Create your hero";
  form.appendChild(title);
  const nameInput = document.createElement("input");
  nameInput.maxLength = 16;
  nameInput.placeholder = "hero name";
  nameInput.autocomplete = "off";
  form.appendChild(nameInput);

  const row = (label: string, body: HTMLElement) => {
    const r = document.createElement("div");
    r.className = "cd-row";
    const tag = document.createElement("span");
    tag.textContent = label;
    r.append(tag, body);
    form.appendChild(r);
  };

  // Body-type toggle (male/female) — the most fundamental identity choice, so
  // it leads the knob rows. Styled like the hair-style chips.
  const sexEls: { btn: HTMLButtonElement; sex: Sex }[] = [];
  {
    const wrap = document.createElement("div");
    wrap.className = "cd-styles";
    for (const sx of SEXES) {
      const b = document.createElement("button");
      b.type = "button";
      b.textContent = sx;
      b.onclick = () => {
        look.sex = sx;
        // Sex-gated hair/beard: keep a shared cut (long), else snap to default;
        // females always clean-shaven.
        look.hairStyle = fitHairStyle(look.hairStyle, sx);
        look.beardStyle = fitBeardStyle(look.beardStyle, sx);
        rebuildStyleChips();
        syncSelection();
        rebuildRig();
      };
      wrap.appendChild(b);
      sexEls.push({ btn: b, sex: sx });
    }
    row("body", wrap);
  }

  // Color swatch rows — buttons carry their color; sync() re-derives .sel from
  // the working look so setLook/randomize can't drift the UI.
  const swatchEls: { key: (typeof COLOR_ROWS)[number]["key"]; btn: HTMLButtonElement; color: number }[] = [];
  for (const { key, label, colors } of COLOR_ROWS) {
    const wrap = document.createElement("div");
    wrap.className = "cd-swatches";
    for (const c of colors) {
      const b = document.createElement("button");
      b.type = "button";
      b.style.background = toCssHex(c);
      b.onclick = () => { look[key] = c; syncSelection(); rebuildRig(); };
      wrap.appendChild(b);
      swatchEls.push({ key, btn: b, color: c });
    }
    row(label, wrap);
  }

  // Hair-style buttons — sex-gated (male: cap/crew/…; female: bun/long/ponytail).
  const styleEls: { btn: HTMLButtonElement; style: HairStyle }[] = [];
  let hairWrap: HTMLDivElement;
  {
    hairWrap = document.createElement("div");
    hairWrap.className = "cd-styles";
    row("hair", hairWrap);
  }

  // Beard-style buttons (male only; uses hair color). Hidden for female.
  const beardEls: { btn: HTMLButtonElement; style: BeardStyle }[] = [];
  let beardRowEl: HTMLDivElement;
  let beardWrap: HTMLDivElement;
  {
    beardWrap = document.createElement("div");
    beardWrap.className = "cd-styles";
    beardRowEl = document.createElement("div");
    beardRowEl.className = "cd-row";
    const tag = document.createElement("span");
    tag.textContent = "beard";
    beardRowEl.append(tag, beardWrap);
    form.appendChild(beardRowEl);
  }

  function rebuildStyleChips(): void {
    hairWrap.replaceChildren();
    styleEls.length = 0;
    for (const s of hairStylesFor(look.sex)) {
      const b = document.createElement("button");
      b.type = "button";
      b.textContent = s === "none" ? "bald" : s;
      b.onclick = () => { look.hairStyle = s; syncSelection(); rebuildRig(); };
      hairWrap.appendChild(b);
      styleEls.push({ btn: b, style: s });
    }
    beardWrap.replaceChildren();
    beardEls.length = 0;
    const beards = beardStylesFor(look.sex);
    beardRowEl.style.display = look.sex === "female" ? "none" : "";
    for (const s of beards) {
      const b = document.createElement("button");
      b.type = "button";
      b.textContent = s === "none" ? "clean" : s;
      b.onclick = () => { look.beardStyle = s; syncSelection(); rebuildRig(); };
      beardWrap.appendChild(b);
      beardEls.push({ btn: b, style: s });
    }
  }
  rebuildStyleChips();

  const randomBtn = document.createElement("button");
  randomBtn.type = "button";
  randomBtn.className = "cd-random";
  randomBtn.textContent = "🎲 surprise me";
  randomBtn.onclick = () => {
    const pick = <T,>(list: readonly T[]) => list[Math.floor(Math.random() * list.length)]!;
    for (const { key, colors } of COLOR_ROWS) look[key] = pick(colors);
    look.sex = pick(SEXES);
    look.hairStyle = pick(hairStylesFor(look.sex));
    look.beardStyle = pick(beardStylesFor(look.sex));
    rebuildStyleChips();
    syncSelection();
    rebuildRig();
  };
  form.appendChild(randomBtn);

  // Pin the error + actions to the panel's bottom edge.
  const spacer = document.createElement("div");
  spacer.className = "cd-spacer";
  form.appendChild(spacer);

  const err = document.createElement("div");
  err.className = "cd-err";
  const actionsRow = document.createElement("div");
  actionsRow.className = "cd-actions";
  let primaryBtn: HTMLButtonElement | null = null;
  let secondaryBtn: HTMLButtonElement | null = null;
  if (opts.actions?.primary) {
    primaryBtn = document.createElement("button");
    primaryBtn.type = "button";
    primaryBtn.className = "cd-primary";
    primaryBtn.textContent = opts.actions.primary.label;
    primaryBtn.onclick = opts.actions.primary.onClick;
    actionsRow.appendChild(primaryBtn);
  }
  if (opts.actions?.secondary) {
    secondaryBtn = document.createElement("button");
    secondaryBtn.type = "button";
    secondaryBtn.className = "cd-secondary";
    secondaryBtn.textContent = opts.actions.secondary.label;
    secondaryBtn.onclick = opts.actions.secondary.onClick;
    actionsRow.appendChild(secondaryBtn);
  }
  form.append(err, actionsRow);
  el.append(previewBox, form);

  // Enter submits; every keydown stops propagation so typing never leaks into
  // in-world game handlers (charselect reopens while joined, via Switch
  // character) or studio hotkeys.
  nameInput.addEventListener("keydown", (e) => {
    e.stopPropagation();
    if (e.key === "Enter") primaryBtn?.click();
  });

  function syncSelection(): void {
    for (const { key, btn, color } of swatchEls) btn.classList.toggle("sel", look[key] === color);
    for (const { btn, style } of styleEls) btn.classList.toggle("sel", look.hairStyle === style);
    for (const { btn, style } of beardEls) btn.classList.toggle("sel", look.beardStyle === style);
    for (const { btn, sex } of sexEls) btn.classList.toggle("sel", look.sex === sex);
  }
  syncSelection();

  // ---- the 3D preview (own GL context — the charselect pattern) -------------
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(FOV, 1, 0.1, 100);
  // Full-screen framing: close enough that the hero fills most of the viewport
  // height, panned so the figure sits right of the floating panel.
  const PAN_X = -0.5;
  camera.position.set(PAN_X, 1.16, 2.6);
  camera.lookAt(PAN_X, 1.04, 0);
  // Noon-ish toon lighting, mirroring daynight so the rig reads like in-world.
  scene.add(new THREE.HemisphereLight(0xbcd7ff, 0x4a5a3a, 0.75));
  const dir = new THREE.DirectionalLight(0xffffff, 1.8);
  dir.position.set(2.4, 4, 3);
  scene.add(dir);
  scene.add(new THREE.AmbientLight(0xffffff, 0.28));

  let rig: THREE.Group | null = null;
  let raf = 0;
  let last = 0;
  let disposed = false;

  function rebuildRig(): void {
    const ry = rig ? rig.rotation.y : 0.35; // keep the current spin — no snap
    if (rig) { scene.remove(rig); disposeGroup(rig); }
    rig = npcMeshGroup("chardesign", (p) => { humanoid(p, lookOptions(look)); });
    rig.rotation.y = ry;
    scene.add(rig);
  }
  rebuildRig();

  function resize(): void {
    const r = previewBox.getBoundingClientRect();
    const w = Math.max(1, Math.round(r.width));
    const h = Math.max(1, Math.round(r.height));
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  // Drag to spin the hero; the idle auto-turn resumes after a short pause.
  let dragging = false;
  let dragX = 0;
  let idleT = 0;
  canvas.addEventListener("pointerdown", (e) => {
    dragging = true;
    dragX = e.clientX;
    canvas.setPointerCapture(e.pointerId);
  });
  canvas.addEventListener("pointermove", (e) => {
    if (!dragging || !rig) return;
    rig.rotation.y += (e.clientX - dragX) * 0.012;
    dragX = e.clientX;
    idleT = 1.6;
  });
  const endDrag = () => { dragging = false; };
  canvas.addEventListener("pointerup", endDrag);
  canvas.addEventListener("pointercancel", endDrag);

  function frame(t: number): void {
    raf = requestAnimationFrame(frame);
    const dt = last ? Math.min(0.05, (t - last) / 1000) : 0;
    last = t;
    if (rig && !dragging) {
      if (idleT > 0) idleT -= dt;
      else rig.rotation.y += dt * 0.55;
    }
    renderer.render(scene, camera);
  }
  const observer = new ResizeObserver(() => resize());
  observer.observe(previewBox);

  return {
    el,
    getLook: () => ({ ...look }),
    setLook(a: Appearance) {
      Object.assign(look, sanitizeAppearance(a));
      rebuildStyleChips();
      syncSelection();
      rebuildRig();
    },
    getName: () => nameInput.value.trim().slice(0, 16),
    setName(v: string) { nameInput.value = v; },
    focusName() { nameInput.focus(); },
    setError(msg: string) { err.textContent = msg; },
    setBusy(busy: boolean) { if (primaryBtn) primaryBtn.disabled = busy; },
    setSecondaryLabel(label: string) { if (secondaryBtn) secondaryBtn.textContent = label; },
    start() { if (!raf && !disposed) { last = 0; resize(); raf = requestAnimationFrame(frame); } },
    stop() { if (raf) { cancelAnimationFrame(raf); raf = 0; } },
    resize,
    dispose() {
      disposed = true;
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      observer.disconnect();
      if (rig) { scene.remove(rig); disposeGroup(rig); rig = null; }
      renderer.dispose();
      el.remove();
    },
  };
}
